import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getOrderStatus } from '../API/OrderAPI';
import { toast } from 'sonner';
import { FaCheckCircle, FaClock, FaRupeeSign, FaDollarSign, FaSpinner } from 'react-icons/fa';
import { MdRestaurant } from 'react-icons/md';
import Breadcrumb from '../components/Breadcrumb';
import Config from '../API/Config';
import confirmGif from '../assets/images/confirm.gif';

const OrderSuccess = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [orderStatus, setOrderStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSellerAssigned, setIsSellerAssigned] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [showRedirect, setShowRedirect] = useState(false);

  const CountryId = Config.getCountryId();
  const currencySymbol = CountryId === 'IN' ? <FaRupeeSign /> : <FaDollarSign />;

  const fetchOrderStatus = async () => {
    try {
      const res = await getOrderStatus(orderId);
      if (res.status === true) {
        setOrderStatus(res.responseData);

        // Check if seller is assigned
        if (res.responseData.isSellerAssigned === true || res.responseData.orderStatus === 'CONFIRMED') {
          if (!isSellerAssigned) {
            setIsSellerAssigned(true);
            setShowRedirect(true);
            toast.success('Order confirmed! Seller has been assigned.');
          }
        }
      }
    } catch (error) {
      console.error('Error fetching order status:', error);
      toast.error('Failed to fetch order status');
    } finally {
      setLoading(false);
    }
  };

  // Polling for order status
  useEffect(() => {
    if (orderId) {
      fetchOrderStatus();

      const interval = setInterval(() => {
        if (!isSellerAssigned) {
          fetchOrderStatus();
        }
      }, 3000); // Poll every 3 seconds

      return () => clearInterval(interval);
    }
  }, [orderId, isSellerAssigned]);

  // Countdown and redirect when seller is assigned
  useEffect(() => {
    if (showRedirect) {
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            navigate(`/order-details/${orderId}`);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [showRedirect, navigate, orderId]);

  if (loading) {
    return (
      <div>
        <Breadcrumb title="Order Success" path="Order Success" />
        <div className="container mx-auto px-4 py-8 md:py-16">
          <div className="max-w-2xl mx-auto text-center">
            <div className="animate-pulse">
              <div className="w-20 h-20 md:w-24 md:h-24 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full mx-auto mb-6"></div>
              <div className="h-6 md:h-8 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded mb-4"></div>
              <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded mb-8 w-3/4 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <Breadcrumb title="Order Success" path="Order Success" />

      <div className="container mx-auto px-4 py-8 md:py-16">
        <div className="max-w-3xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-6 md:mb-8 animate-slideDown">
            <div className="relative inline-block">
              {isSellerAssigned ? (
                <>
                  <img src={confirmGif} alt="Success" className="w-24 h-24 md:w-32 md:h-32 mx-auto mb-4" />
                  <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2 w-6 h-6 md:w-8 md:h-8 bg-green-500 rounded-full flex items-center justify-center animate-bounce">
                    <FaCheckCircle className="text-white text-sm md:text-lg" />
                  </div>
                </>
              ) : (
                <div className="relative">
                  <div className="w-24 h-24 md:w-32 md:h-32 mx-auto mb-4 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                    <FaSpinner className="text-white text-3xl md:text-5xl animate-spin" />
                  </div>
                  <div className="absolute inset-0 w-24 h-24 md:w-32 md:h-32 mx-auto rounded-full border-2 md:border-4 border-yellow-300 animate-pulse"></div>
                </div>
              )}
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-green-600 mb-2 px-4">
              {isSellerAssigned ? 'Order Placed Successfully!' : 'Order Pending'}
            </h1>
            <p className="text-contentcolor text-base md:text-lg px-4">
              {isSellerAssigned
                ? 'Your order has been confirmed and is being prepared'
                : 'Your order is awaiting seller assignment'
              }
            </p>
          </div>

          {/* Order Status Card */}
          <div className="bg-white rounded-xl md:rounded-2xl shadow-xl border border-[#e8e8e8] p-4 md:p-6 mb-4 md:mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 md:mb-6 gap-3">
              <h2 className="text-lg md:text-2xl font-semibold text-darktext break-all">
                Order #{orderStatus?.orderNo || orderId}
              </h2>
              <div className={`flex items-center px-3 py-2 md:px-4 md:py-2 rounded-full text-sm md:text-base ${isSellerAssigned
                  ? 'bg-green-100 text-green-800'
                  : 'bg-yellow-100 text-yellow-800'
                }`}>
                {isSellerAssigned ? (
                  <>
                    <FaCheckCircle className="mr-2 text-sm md:text-base" />
                    <span className="font-medium">Confirmed</span>
                  </>
                ) : (
                  <>
                    <FaSpinner className="mr-2 animate-spin text-sm md:text-base" />
                    <span className="font-medium">Pending</span>
                  </>
                )}
              </div>
            </div>

            {/* Status Message */}
            <div className={`border rounded-lg md:rounded-xl p-4 md:p-6 mb-4 md:mb-6 ${isSellerAssigned
                ? 'bg-gradient-to-r from-green-50 to-blue-50 border-green-200'
                : 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200'
              }`}>
              <div className="flex items-start">
                <div className="relative flex-shrink-0">
                  {isSellerAssigned ? (
                    <MdRestaurant className="text-green-600 text-xl md:text-2xl mt-1 mr-3 md:mr-4" />
                  ) : (
                    <FaClock className="text-yellow-600 text-xl md:text-2xl mt-1 mr-3 md:mr-4 animate-pulse" />
                  )}
                </div>
                <div className="flex-1">
                  {isSellerAssigned ? (
                    <>
                      <h3 className="font-semibold text-green-800 mb-2 text-base md:text-lg">Order Confirmed!</h3>
                      <p className="text-green-700 text-sm md:text-base leading-relaxed mb-2">
                        Great! A seller has been assigned to your order and will start preparing it now.
                      </p>
                      {showRedirect && (
                        <p className="text-green-600 text-sm md:text-base font-medium">
                          Redirecting to order details in {countdown} seconds...
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <h3 className="font-semibold text-yellow-800 mb-2 text-base md:text-lg">Order Pending</h3>
                      <p className="text-yellow-700 text-sm md:text-base leading-relaxed">
                        Your order is being processed and we're finding the best seller to fulfill it.
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Order Details */}
            {orderStatus && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 text-sm">
                <div className="flex items-center">
                  <FaClock className="text-theme2 mr-2 flex-shrink-0" />
                  <div>
                    <span className="text-contentcolor block text-xs md:text-sm">Status:</span>
                    <p className="font-semibold text-base md:text-lg">{orderStatus.orderStatus}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <FaCheckCircle className={`mr-2 flex-shrink-0 ${
                    orderStatus.isSellerAssigned === true ? 'text-green-500' : 'text-gray-400'
                  }`} />
                  <div>
                    <span className="text-contentcolor block text-xs md:text-sm">Seller Assigned:</span>
                    <p className="font-semibold text-base md:text-lg">
                      {orderStatus.isSellerAssigned === true ? 'Yes' : 'No'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {isSellerAssigned && (
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <Link
                to={`/order-details/${orderId}`}
                className="flex-1 bg-theme2 text-white py-3 md:py-4 px-4 md:px-6 rounded-full text-center hover:bg-black transition-colors duration-300 font-semibold text-sm sm:text-base md:text-lg"
              >
                View Order Details
              </Link>
              <Link
                to="/menu"
                className="flex-1 border-2 border-theme2 text-theme2 py-3 md:py-4 px-4 md:px-6 rounded-full text-center hover:bg-theme2 hover:text-white transition-colors duration-300 font-semibold text-sm sm:text-base md:text-lg"
              >
                Continue Shopping
              </Link>
            </div>)}
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;